import { SearchableSelect, SearchableSelectOption } from "@comps/ui/searchable-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { AggregationType, columnAggregation } from "@dto/fields";
import { ObjectInfos } from "@dto/objects";
import { Summary, View } from "@dto/views";
import { BASE_URL } from "@utils/constants";
import { getPathKeys, getSrcFieldKey } from "@utils/model";
import { DEFAULT_SUMMARY_AGGREGATION, isSummable, removeSummary, SUMMARY_AGGREGATIONS, summaryRowKeys, upsertSummary } from "@utils/summaries";
import { toastError } from "@utils/toasts";
import { TW_FilterEditorNewFilter, TW_FilterEditorRemoveFilter, TW_FilterEditorRemoveFilterDisabled, TW_SummaryEditor, TW_SummaryEditorAggregationSelect, TW_SummaryEditorPathSelect } from "@utils/twClasses";
import { put } from "@utils/utils";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useModelStore } from "src/store";


interface SummaryEditorProps {
    summaries: Summary[]
    disabled: boolean
    onClose: () => void
}

function SummaryEditor({ summaries, disabled, onClose }: SummaryEditorProps) {

    const workspace = useModelStore((state) => state.selectedWorkspace);
    const updateSelectedView = useModelStore((state) => state.updateSelectedView);
    const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
    const sharedObjectInfos = useModelStore((state) => state.sharedObjectInfos);
    const selectedView = useModelStore((state) => state.selectedView);
    const { t } = useTranslation();

    const fields = selectedObjectInfos?.fields;
    if (!fields) {
        return null;
    }

    const summaryPathKeys = summaries.map((summary) => summary.pathKey);

    const onViewChange = (objectInfos: ObjectInfos, view: View) => {
        updateSelectedView(objectInfos, view, null, sharedObjectInfos);
        // No need to call fetchView as it is triggered by the view change
    }

    const onRemoveSummary = (summaryPathKey: string) => {
        if (!summaryPathKey || disabled) {
            return;
        }

        validateAllSummaries(removeSummary(summaries, summaryPathKey));
    }

    function validateSummary(summary: Summary | null) {
        if (!summary) {
            return;
        }

        validateAllSummaries(upsertSummary(summaries, summary));
    }

    function validateAllSummaries(summaries: Summary[] | null) {
        if (!workspace?._key) {
            console.error("Workspace key is not set");
            return;
        }
        const newView: View = {
            ...selectedView!,
            summaries: summaries
        }
        put<View>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${selectedObjectInfos?.object?._key}/views`, newView).then(response => {
            if (response) {
                if (isCompozzError(response)) {
                    const cError = response as CompozzError;
                    if (cError.i18n) {
                        toastError(translate(t, cError.i18n, cError.params));
                    } else {
                        toastError(cError.message);
                    }
                    onClose();
                }
            } else {
                onViewChange(selectedObjectInfos!, newView);
            }
        })
    }

    const rowKeys = summaryRowKeys(selectedView?._key + "-summary-", summaries);
    if (disabled) {
        return (
            <TooltipProvider>
                <Tooltip delayDuration={100}>
                    <TooltipTrigger>
                        <div className={TW_SummaryEditor}>
                            {summaries.map((summary, index) => {
                                return <SummaryItem key={rowKeys.itemKeys[index]} summary={summary} validateSummary={validateSummary} onRemoveSummary={onRemoveSummary} usedSummaryPathKeys={summaryPathKeys} disabled={disabled} />
                            })}
                            <SummaryItem key={rowKeys.newRowKey} summary={null} validateSummary={validateSummary} onRemoveSummary={onRemoveSummary} usedSummaryPathKeys={summaryPathKeys} disabled={disabled} />
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>
                        {t("summary.edition.forbidden.tooltip")}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        )
    } else {
        return (
            <div className={TW_SummaryEditor}>
                {summaries.map((summary, index) => {
                    return <SummaryItem key={rowKeys.itemKeys[index]} summary={summary} validateSummary={validateSummary} onRemoveSummary={onRemoveSummary} usedSummaryPathKeys={summaryPathKeys} disabled={disabled} />
                })}
                <SummaryItem key={rowKeys.newRowKey} summary={null} validateSummary={validateSummary} onRemoveSummary={onRemoveSummary} usedSummaryPathKeys={summaryPathKeys} disabled={disabled} />
            </div>
        )
    }
}

interface SummaryItemProps {
    summary: Summary | null,
    validateSummary: (summary: Summary) => void,
    onRemoveSummary: (summaryPathKey: string) => void,
    usedSummaryPathKeys: string[],
    disabled: boolean
}

function SummaryItem({ summary, validateSummary, onRemoveSummary, usedSummaryPathKeys, disabled }: SummaryItemProps) {

    const { t } = useTranslation();
    const selectedView = useModelStore((state) => state.selectedView);
    const [selectedPathKey, setSelectedPathKey] = useState<string | undefined>(undefined);
    const [selectedAggregation, setSelectedAggregation] = useState<AggregationType>(DEFAULT_SUMMARY_AGGREGATION);
    const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
    const relationByKeys = useModelStore((state) => state.relationByKeys);
    const fieldByKeys = useModelStore((state) => state.fieldByKeys);

    useEffect(() => {
        if (summary) {
            const fields = selectedObjectInfos?.fields;
            if (!fields) {
                return;
            }
            const pathKeys = getPathKeys(selectedObjectInfos, fields, relationByKeys, fieldByKeys, true);
            const selectedPathKeyIndex = pathKeys.findIndex(pathKey => pathKey === summary.pathKey);
            if (selectedPathKeyIndex > -1) {
                setSelectedPathKey(summary.pathKey);
            } else {
                const fieldKey = getSrcFieldKey(summary.pathKey);
                setSelectedPathKey(fieldKey?.fieldKey);
            }

            setSelectedAggregation(summary.aggregation ?? DEFAULT_SUMMARY_AGGREGATION);
        }
    }, [summary]);

    const onPathChange = (pathKey: string) => {
        setSelectedPathKey(pathKey);

        const newSummary: Summary = {
            pathKey: pathKey,
            aggregation: selectedAggregation
        }
        validateSummary(newSummary);
    }

    const onAggregationChange = (aggregation: AggregationType) => {
        setSelectedAggregation(aggregation);

        if (!selectedPathKey) {
            return;
        }

        const newSummary: Summary = {
            pathKey: selectedPathKey,
            aggregation: aggregation
        }
        validateSummary(newSummary);
    }

    const onRemoveCurrentSummary = () => {
        if (!selectedPathKey || disabled) {
            return;
        }
        onRemoveSummary(selectedPathKey);
    }

    const fields = selectedObjectInfos?.fields;
    if (!fields) {
        return null;
    }
    const pathKeys = getPathKeys(selectedObjectInfos, fields, relationByKeys, fieldByKeys, false);

    const disabledTrash = disabled || !selectedPathKey;

    const divKey = selectedView?._key + "-summary-" + (selectedPathKey ? selectedPathKey + "-" + selectedAggregation : "new");
    return (
        <div className={TW_FilterEditorNewFilter} key={divKey}>
            <SearchableSelect
                className={TW_SummaryEditorPathSelect}
                placeholder={t("summary.path.placeholder")}
                value={selectedPathKey}
                onValueChange={onPathChange}
                disabled={disabled || selectedPathKey != undefined}
                options={pathKeys.flatMap((pathKey, index): SearchableSelectOption[] => {
                    const field = fields[index];
                    if (!isSummable(field, columnAggregation(field, selectedView?.aggregations, pathKey))) {
                        return [];
                    }
                    if (!pathKey) {
                        return [{ value: field._key, label: field.name + " (" + t("group.field.invalid") + ")", disabled: true }];
                    }
                    const disabled = usedSummaryPathKeys.includes(pathKey);
                    return [{ value: pathKey, label: field.name, disabled: disabled }];
                })}
            />
            <Select onValueChange={onAggregationChange} value={selectedAggregation} disabled={disabled || !selectedPathKey}>
                <SelectTrigger className={TW_SummaryEditorAggregationSelect}>
                    <SelectValue placeholder={t("summary.aggregation.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                    {SUMMARY_AGGREGATIONS.map((aggregation) => {
                        return <SelectItem key={divKey + "-aggregation-" + aggregation} value={aggregation}>{t("field.aggregation.label." + aggregation)}</SelectItem>
                    })}
                </SelectContent>
            </Select>
            {!disabledTrash && <Trash2 className={TW_FilterEditorRemoveFilter} onClick={onRemoveCurrentSummary} />}
            {disabledTrash && <Trash2 className={TW_FilterEditorRemoveFilterDisabled} />}
        </div>
    )
}

export default SummaryEditor;
